import { useEffect, useState, type FormEvent } from 'react';
import { Search, X } from 'lucide-react';
import type { GistPersonView, GistPostView, GistSearchReply, GistTagView } from '@london-life/contracts';
import { describeError } from '@london-life/errors';
import { api } from '../lib/api';
import { notify } from '../lib/alerts';
import { GistPostCard } from './post-card';
import { GistAvatar, GistEmpty, compact, type GistNav } from './parts';

// Follow / Following, as Lagos
export function GistFollowButton({
  lifeId,
  on,
  onChange,
}: {
  lifeId: string;
  on: boolean;
  onChange: (on: boolean) => void;
}) {
  const [busy, setBusy] = useState(false);
  const flip = async () => {
    setBusy(true);
    onChange(!on);
    try {
      await api.gist.follow(lifeId, !on);
    } catch (error) {
      onChange(on);
      notify(describeError(error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      className={`gist-follow${on ? ' on' : ''}`}
      aria-pressed={on}
      disabled={busy}
      onClick={(event) => {
        event.stopPropagation();
        void flip();
      }}
    >
      {on ? 'Following' : 'Follow'}
    </button>
  );
}

// a player in the results: their page on a tap, and Follow
function PersonRow({ person, nav }: { person: GistPersonView; nav: GistNav }) {
  const [following, setFollowing] = useState(person.following);
  return (
    <li className="gist-person">
      <button type="button" className="gist-person-open" onClick={() => nav.user(person.lifeId, person.name)}>
        <GistAvatar author={person} />
        <strong>{person.name}</strong>
      </button>
      <GistFollowButton lifeId={person.lifeId} on={following} onChange={setFollowing} />
    </li>
  );
}

// Search, as Lagos: Trending in London (#tags) until you search; then players and posts for words, a #tag or
// an @name
export function GistSearch({ q, setQ, nav }: { q: string; setQ: (q: string) => void; nav: GistNav }) {
  const [text, setText] = useState(q);
  const [trends, setTrends] = useState<GistTagView[] | null>(null);
  const [found, setFound] = useState<(GistSearchReply & { q: string }) | null>(null);
  useEffect(() => {
    api.gist
      .trends()
      .then((reply) => setTrends(reply.tags))
      .catch(() => setTrends([]));
  }, []);
  useEffect(() => {
    setText(q);
    if (!q.trim()) return setFound(null);
    let live = true;
    api.gist
      .search(q.trim())
      .then((reply) => live && setFound({ ...reply, q: q.trim() }))
      .catch((error: unknown) => live && notify(describeError(error).message));
    return () => {
      live = false;
    };
  }, [q]);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setQ(text.trim());
  };
  const change = (id: string, next: GistPostView | null) =>
    setFound((now) =>
      now
        ? {
            ...now,
            posts: next
              ? now.posts.map((item) => (item.id === id ? next : item))
              : now.posts.filter((item) => item.id !== id),
          }
        : now,
    );
  return (
    <div className="gist-search">
      <form className="gist-search-bar" role="search" onSubmit={submit}>
        <label>
          <Search aria-hidden="true" />
          <input
            value={text}
            maxLength={60}
            enterKeyHint="search"
            placeholder="Search London Gist"
            aria-label="Search London Gist"
            onChange={(event) => setText(event.target.value)}
          />
          {text && (
            <button
              type="button"
              aria-label="Clear"
              onClick={() => {
                setText('');
                setQ('');
              }}
            >
              <X aria-hidden="true" />
            </button>
          )}
        </label>
      </form>
      {!found && (
        <section aria-label="Trending in London">
          <h3 className="gist-title">Trending in London</h3>
          {trends === null && <p className="gist-loading">Loading…</p>}
          {trends?.length === 0 && <p className="gist-loading">No #tags trending yet. Start one.</p>}
          <ol className="gist-trends">
            {trends?.map((tag, index) => (
              <li key={tag.tag}>
                <button type="button" onClick={() => setQ(`#${tag.tag}`)}>
                  <small>{index + 1} · Trending</small>
                  <strong>#{tag.tag}</strong>
                  <small>
                    {compact(tag.posts)} {tag.posts === 1 ? 'post' : 'posts'}
                  </small>
                </button>
              </li>
            ))}
          </ol>
        </section>
      )}
      {found && (
        <>
          {found.people.length > 0 && (
            <ul className="gist-people" aria-label="Players">
              {found.people.map((person) => (
                <PersonRow key={person.lifeId} person={person} nav={nav} />
              ))}
            </ul>
          )}
          {found.posts.length > 0 && (
            <ul className="gist-list" aria-label="Posts">
              {found.posts.map((post) => (
                <GistPostCard
                  key={post.id}
                  post={post}
                  nav={nav}
                  onChange={(next) => change(post.id, next)}
                  onGone={() => change(post.id, null)}
                />
              ))}
            </ul>
          )}
          {!found.people.length && !found.posts.length && (
            <GistEmpty title={`No results for "${found.q}"`} body="Try another word, a #tag or an @name." />
          )}
        </>
      )}
    </div>
  );
}
